import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ClipboardIcon,
  DeleteIcon,
  DragIcon,
  UserIcon,
} from '@sketchnote/icons';
import { ELEMENT_BOARD, ELEMENT_MULTI_COL, ELEMENT_TABLE_PLUS } from '@sketchnote/slate-plugins';
import {
  ELEMENT_DEFAULT,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_OL,
  ELEMENT_UL,
  findNode,
  insertNodes,
  setNodes,
  TDescendant,
  toggleList,
  toggleNodeType,
  usePlateEditorRef,
} from '@udecode/plate';
import cn from 'classnames';
import * as React from 'react';
import { Editor, Node, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { WeaponsList } from 'src/components/BlockEditor/configs';

import { boardData, tablePlusData } from '../../BlockCombobox';

const { useCallback, useMemo, Fragment, forwardRef } = React;

const PopupToolBar = ({
  editor,
  element,
  onLeave,
}: {
  editor: any;
  element: any;
  onLeave: () => void;
}) => {
  const items = useMemo(
    () =>
      (WeaponsList || []).map((obj, index) => ({
        key: `${index}`,
        text: obj.name,
        data: obj,
      })),
    [WeaponsList]
  );

  const handleSelectNewNodeType = (editor, item) => {
    onLeave();
    const itemType = item.data.type;
    const currentpath = ReactEditor.findPath(editor, element);
    if (currentpath.length) {
      const [match] = Editor.nodes(editor, {
        match: (n: any) => Editor.isBlock(editor, n),
      });
      const path = match?.[1] || [editor.children.length - 1];
      const node = findNode(editor, { at: path })?.[0];
      const content = node ? Node.string(node) : '';
      if (itemType === ELEMENT_TABLE_PLUS) {
        if (content.length > 0) {
          insertNodes(
            editor,
            {
              type: ELEMENT_TABLE_PLUS,
              children: [{ text: '' }],
              column: tablePlusData.columns,
              row: tablePlusData.rows,
            },
            { at: Path.next(path) }
          );
        } else {
          setNodes(
            editor,
            {
              type: ELEMENT_TABLE_PLUS,
              children: [{ text: '' }],
              column: tablePlusData.columns,
              row: tablePlusData.rows,
            },
            { at: path }
          );
        }
      } else if (itemType === ELEMENT_BOARD) {
        if (content.length > 0) {
          insertNodes(
            editor,
            {
              type: ELEMENT_BOARD,
              children: [{ text: '' }],
              columns: boardData.columns,
            },
            { at: Path.next(path) }
          );
        } else {
          setNodes(
            editor,
            {
              type: ELEMENT_BOARD,
              children: [{ text: '' }],
              columns: boardData.columns,
            },
            { at: path }
          );
        }
      } else if (itemType === ELEMENT_UL || itemType === ELEMENT_OL) {
        toggleList(editor, { type: itemType });
      } else {
        toggleNodeType(editor, { activeType: itemType || 'p' });
      }
    }
  };

  return (
    <div
      className="absolute w-304 max-h-320 overflow-scroll shadow-xs bg-white rounded-sm z-100"
      onMouseLeave={onLeave}
    >
      {items.map((item) => (
        <button
          key={item.key}
          onClick={() => handleSelectNewNodeType(editor, item)}
          className={cn(
            'text-gray-600 flex items-center p-12 hover:bg-yellow-150 cursor-pointer w-full'
          )}
        >
          <div className="flex items-center">
            <span className="mr-8 text-5xl flex">{item.data?.icon && <item.data.icon />}</span>
            <div className="text-xs flex flex-col text-left">
              <span className={cn('text-black')}>{item.data.name}</span>
              <span className="text-gray-500">{item.data.subText}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

// eslint-disable-next-line react/display-name
export const BlockToolbar = React.memo(
  ({
    element,
    isMenuOpened,
    setIsMenuOpened,
  }: {
    element: TDescendant;
    isMenuOpened: boolean;
    setIsMenuOpened: (value: boolean) => void;
  }): JSX.Element => {
    const editor = usePlateEditorRef();
    const [isComboboxOpen, setOpenCombobox] = React.useState<boolean>(false);
    const handleDeleteNode = useCallback(() => {
      const path = ReactEditor.findPath(editor, element);
      Transforms.removeNodes(editor, { at: path });
    }, [element, editor]);

    const handleAddNewNode = useCallback(() => {
      try {
        const path = ReactEditor.findPath(editor, element);
        const at: Path = Path.next(path);

        insertNodes(
          editor,
          {
            type: ELEMENT_DEFAULT,
            children: [{ text: '' }],
          },
          { at }
        );
        // Code to open the combobox for default empty elements
        // if (
        //   Node.string(element) === '' &&
        //   [ELEMENT_DEFAULT, ELEMENT_H1, ELEMENT_H2, ELEMENT_H3].includes(element.type)
        // ) {
        //   setOpenCombobox(true);
        // } else {
        //   insertNodes(
        //     editor,
        //     {
        //       type: ELEMENT_DEFAULT,
        //       children: [{ text: '' }],
        //     },
        //     { at }
        //   );
        // }
      } catch (error) {
        console.log(error);
      }
    }, [element, editor]);

    const handleDuplicateNode = useCallback(() => {
      const path = ReactEditor.findPath(editor, element);
      const at = Path.next(path);
      const newElement = { ...element };
      if (newElement.id) {
        delete newElement.id;
      }
      Transforms.insertNodes(editor, newElement as any, {
        at,
      });
    }, [editor, element]);
    const handleMenuOpen = () => {
      setIsMenuOpened(true);
    };
    const handleMenuClose = () => {
      setIsMenuOpened(false);
    };
    const handleToggleAssignPeople = useCallback(() => {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(
        editor,
        { isAssign: !element.isAssign, assignedDates: [], members: [] } as any,
        { at: path }
      );
      handleMenuClose();
    }, [element, editor]);

    const popover = useMemo(() => {
      return (
        <Popover
          closeOnBlur
          onClose={handleMenuClose}
          onOpen={handleMenuOpen}
          isOpen={isMenuOpened}
          placement="left-start"
          modifiers={[{ name: 'eventListeners', options: { scroll: isMenuOpened } }]}
        >
          {({ isOpen }) => (
            <Fragment>
              <div className="relative">
                <button id="clickAdd" onClick={handleAddNewNode} className="text-gray-500">
                  <AddIcon />
                </button>
                {isComboboxOpen && (
                  <PopupToolBar
                    editor={editor}
                    element={element}
                    onLeave={() => setOpenCombobox(false)}
                  />
                )}
              </div>
              <PopoverTrigger>
                <button className="w-full text-left flex justify-between items-center text-gray-500">
                  <span
                    className={cn({
                      'px-8 py-1 rounded': true,
                    })}
                  >
                    <DragIcon size={8} />
                  </span>
                </button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent className="p-6 bg-white shadow-lg rounded-md max-h-304 min-w-160 overflow-auto ">
                  {isOpen ? (
                    <Fragment>
                      {element.type === 'micro_task' ? (
                        <Fragment>
                          <Item
                            leftIcon={UserIcon}
                            onClick={handleToggleAssignPeople}
                            label={element.isAssign ? 'Remove Assign' : 'Assign'}
                          />
                          <div className="border-b border-gray-200 my-5" />
                        </Fragment>
                      ) : null}
                      {(editor.children.length > 1 ||
                        editor.children?.[0].type === ELEMENT_MULTI_COL) && (
                        <Item leftIcon={DeleteIcon} onClick={handleDeleteNode} label="Delete" />
                      )}
                      <Item
                        leftIcon={ClipboardIcon}
                        onClick={handleDuplicateNode}
                        label="Duplicate"
                      />
                    </Fragment>
                  ) : null}
                </PopoverContent>
              </Portal>
            </Fragment>
          )}
        </Popover>
      );
    }, [handleAddNewNode, handleDeleteNode, handleDuplicateNode, isMenuOpened, isComboboxOpen]);
    return <>{popover}</>;
  }
);

export interface ItemProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  leftIcon: ({ className }: { className: string }) => JSX.Element;
  label?: string;
  isRightArrow?: boolean;
}

export const Item = forwardRef<HTMLButtonElement, ItemProps>(
  // eslint-disable-next-line react/prop-types
  ({ isRightArrow, label, leftIcon: LeftIcon, ...otherProps }, ref) => {
    return (
      <button
        ref={ref}
        className="px-8 w-full text-left py-7 rounded-sm min-w-btn-lg hover:bg-gray-100 cursor-pointer align-middle justify-between flex items-center font-regular"
        {...otherProps}
      >
        <span className="flex items-center">
          {<LeftIcon className="mr-8 text-lg text-gray-500" />}
          <span className="text-sm">{label}</span>
        </span>
        {isRightArrow ? (
          <span
            style={{
              transform: 'rotate(-90deg)',
              transition: 'all 0.5s',
            }}
          >
            <ChevronDownIcon className="text-gray-200" />
          </span>
        ) : null}
      </button>
    );
  }
);

Item.displayName = 'Item';
